import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Search, RefreshCw, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Flat "Sent" feed — one row per outgoing message, sorted newest-first.
 *
 * This is Gmail's mental model for the Sent folder: each row shows
 * **who we sent it to**, **the subject we sent**, **when we sent it**,
 * and **a preview** — not the latest received message in that thread.
 * Clicking a row opens the parent thread in the existing detail pane.
 */
function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    const yr = d.getFullYear() === now.getFullYear() ? undefined : "numeric";
    return d.toLocaleDateString([], { month: "short", day: "numeric", year: yr });
  } catch {
    return "";
  }
}

function recipientLabel(msg) {
  const list = msg.to?.length ? msg.to : (msg.to_emails || []).map((email) => ({ email }));
  if (!list.length) return "(no recipients)";
  const first = list[0];
  const firstName = (first?.name && first.name.trim()) || first?.email || "Unknown";
  if (list.length === 1) return firstName;
  return `${firstName} +${list.length - 1}`;
}

export default function SentList({ selectedId, onSelect, onOpenSidebar, compact = false }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (!search.trim()) setDebouncedSearch("");
  }, [search]);
  const submitSentSearch = (e) => { e.preventDefault(); setDebouncedSearch(search.trim()); };

  const seqRef = useRef(0);
  const fetchPage = useCallback(async (opts = {}) => {
    const { append = false, before = null } = opts;
    const seq = ++seqRef.current;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { limit: 100 };
      if (before) params.before = before;
      if (debouncedSearch) params.q = debouncedSearch;
      const { data } = await api.get("/messages/sent", { params });
      if (seq !== seqRef.current) return;
      const newItems = data?.items || [];
      setItems((prev) => append ? [...prev, ...newItems] : newItems);
      setCursor(data?.next_cursor || null);
    } catch {
      // Soft-fail: keep whatever we had; the toast in the parent is enough.
    } finally {
      if (seq === seqRef.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [debouncedSearch]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const visible = items;

  return (
    <div className="flex flex-col h-full w-full bg-white border-r border-zinc-200" data-testid="sent-list-wrapper">
      {/* Header */}
      <div className="px-3 sm:px-4 py-2.5 border-b border-zinc-200 flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onOpenSidebar}
          className="md:hidden p-1.5 -ml-1 rounded hover:bg-zinc-100"
          aria-label="Open sidebar"
        >
          <Send className="w-4 h-4 text-zinc-600" strokeWidth={1.75} />
        </button>
        <Send className="w-4 h-4 text-zinc-600 hidden md:block" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-900">Sent</p>
          <p className="text-[11px] text-zinc-500">{items.length.toLocaleString()} outgoing {items.length === 1 ? "message" : "messages"}</p>
        </div>
        <Button
          variant="ghost" size="sm" className="h-8 px-2"
          onClick={() => fetchPage()}
          disabled={loading}
          data-testid="sent-refresh"
          title="Refresh"
        >
          <RefreshCw className={"w-3.5 h-3.5 " + (loading ? "animate-spin" : "")} strokeWidth={1.75} />
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 sm:px-4 py-2 border-b border-zinc-100 shrink-0">
        <form className="flex items-center gap-2" onSubmit={submitSentSearch} data-testid="sent-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" strokeWidth={1.75} />
          <Input
            type="search"
            enterKeyHint="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sent messages…"
            className="pl-8 h-8 text-sm bg-zinc-50 border-zinc-200 focus-visible:ring-1 focus-visible:ring-[#0033CC]"
            data-testid="sent-search-input"
          />
        </div>
          <button
            type="submit"
            disabled={!search.trim()}
            className="shrink-0 h-8 px-3.5 rounded-full bg-[#0033CC] text-white text-xs font-medium hover:bg-[#0029A3] active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed transition-[background-color,transform,opacity] duration-150"
            data-testid="sent-search-submit-button"
          >
            Search
          </button>
        </form>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && items.length === 0 ? (
          <div className="text-sm text-zinc-500 py-10 text-center">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-zinc-500" data-testid="sent-empty">
            {debouncedSearch
              ? "No sent messages match your search."
              : "No outgoing messages yet. Replies and forwards your team sends will show up here."}
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {visible.map((m) => {
              const active = selectedId === m.thread_id;
              const subject = m.subject || m.thread_subject || "(no subject)";
              return (
                <li
                  key={m.id}
                  onClick={() => onSelect(m.thread_id)}
                  role="button"
                  className={
                    "px-3 sm:px-4 py-2.5 cursor-pointer transition-colors min-w-0 " +
                    (active ? "bg-[#C2DBFF]" : "bg-white hover:bg-zinc-50")
                  }
                  data-testid={`sent-row-${m.id}`}
                >
                  <div className="flex items-baseline justify-between gap-3 mb-0.5">
                    <p className="text-[13px] text-zinc-500 truncate">
                      <span className="text-zinc-400 font-mono mr-1">To</span>
                      <span className="text-zinc-700 font-medium">{recipientLabel(m)}</span>
                    </p>
                    <p className="text-[11px] text-zinc-400 font-mono shrink-0">{timeAgo(m.sent_at)}</p>
                  </div>
                  <p className={"text-sm truncate " + (compact ? "" : "mb-0.5") + " text-zinc-900 font-medium"}>{subject}</p>
                  {!compact && m.preview && (
                    <p className="text-xs text-zinc-500 truncate">{m.preview}</p>
                  )}
                  {m.send_status && m.send_status !== "sent" && (
                    <p className="text-[10px] text-amber-600 font-medium uppercase tracking-wide mt-1">
                      {m.send_status}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {cursor && (
          <div className="border-t border-zinc-100 p-2.5 text-center">
            <Button
              variant="ghost" size="sm" className="h-8 text-xs"
              onClick={() => fetchPage({ append: true, before: cursor })}
              disabled={loadingMore}
              data-testid="sent-load-more"
            >
              {loadingMore ? "Loading…" : "Load older messages"}
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" strokeWidth={1.5} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
